import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { Loader2, Printer, X } from "lucide-react";
import { toast } from "sonner";
import { getHttpStatusAndMessage } from "@/lib/network-utils";
import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { differenceInYears } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import type { ChildWithAttendance, AttendanceStatus, ChildBase } from "nautika-types";
import { ChildAttendanceCard } from "@/features/dash/home/components/child-attendance-card";
import { AddChildCombobox } from "@/features/dash/home/components/add-child-combobox";
import { ChildForm } from "@/features/dash/registrations/forms/child-form";
import { authClient } from "@/lib/auth-client";

interface AttendanceSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  periodTitle: string;
  periodDate: string;
  periodTime: string; // (actuellement non affiché dans le design final)
  periodId: string; // ID de la période pour récupérer les données
  date: string; // Date au format YYYY-MM-DD
  targetGroupId?: string; // utilisé seulement pour priorisation potentielle future
  onChildrenCountChange?: (info: { periodId: string; date: string; count: number }) => void; // callback vers le planning
}

export function AttendanceSheet({ open, onOpenChange, periodTitle, periodDate, periodTime, periodId, date, onChildrenCountChange }: AttendanceSheetProps) {
  // Les enfants ajoutés de façon optimiste doivent toujours apparaître en dernier et y rester.
  const [selectedGroup, setSelectedGroup] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [stableChildrenOrder, setStableChildrenOrder] = useState<string[]>([]);
  const [optimisticAttendance, setOptimisticAttendance] = useState<Record<string, AttendanceStatus | undefined>>({});
  const [optimisticAdded, setOptimisticAdded] = useState<ChildWithAttendance[]>([]);
  const [recentlyAddedIds, setRecentlyAddedIds] = useState<Set<string>>(new Set());
  const [optimisticRemovedIds, setOptimisticRemovedIds] = useState<Set<string>>(new Set());
  // Ordre d'insertion des enfants adHoc (ajout rapide) pour conserver l'ordre d'ajout en bas de liste
  const [adHocOrder, setAdHocOrder] = useState<Record<string, number>>({});
  const adHocOrderCounter = useRef(0);
  // Id d'un enfant vers lequel on doit scroller après un ajout inter-groupe
  const [scrollTargetChildId, setScrollTargetChildId] = useState<string | undefined>(undefined);
  // Refs DOM des cartes enfant pour scroll précis
  const childRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // État pour sheet de création / consultation rapide d'un enfant
  const [childFormOpen, setChildFormOpen] = useState(false);
  const [childFormMode, setChildFormMode] = useState<"create" | "view" | "edit">("create");
  const [childDefaultGroup, setChildDefaultGroup] = useState<string | undefined>(undefined);
  const [viewingChild, setViewingChild] = useState<ChildWithAttendance | undefined>(undefined);

  // Déterminer si la date est dans le futur (comparaison au jour près, en tenant compte du fuseau local)
  const isFutureDay = useMemo(() => {
    try {
      if (!date) return false;
      const [y, m, d] = date.split("-").map(Number);
      if (!y || !m || !d) return false;
      const target = new Date(y, (m || 1) - 1, d || 1); // local minuit
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return target.getTime() > today.getTime();
    } catch {
      return false;
    }
  }, [date]);

  const handleOpenCreateChild = useCallback((groupId: string) => {
    setChildDefaultGroup(groupId || undefined);
    setViewingChild(undefined);
    setChildFormMode("create");
    setChildFormOpen(true);
  }, []);

  const handleViewChild = useCallback((child: ChildWithAttendance) => {
    setViewingChild(child);
    setChildFormMode("view");
    setChildFormOpen(true);
  }, []);

  // Récupérer les groupes d'âge
  const { data: ageGroupsData } = api.useQuery("get", "/club/age-groups", {
    enabled: open, // Ne charger que quand la sheet est ouverte
    staleTime: 300000, // Cache pendant 5 minutes
  });

  const queryClient = useQueryClient();
  const { data: activeOrg } = authClient.useActiveOrganization();

  // Récupérer les données de la période
  const {
    data: periodData,
    error,
    isLoading,
  } = api.useQuery("get", "/attendance/period/{periodId}", {
    params: {
      path: { periodId },
      query: { date },
    },
    enabled: open, // Ne charger que quand la sheet est ouverte
    staleTime: 0, // Toujours refetch pour avoir les données à jour
    refetchOnWindowFocus: false, // Éviter les refetch quand on revient sur la fenêtre
    refetchOnMount: true, // Toujours refetch à l'ouverture de la sheet
  });

  // Mutation serveur (UI déjà optimiste via state local)
  const updateAttendanceMutation = api.useMutation("patch", "/attendance/reservation/{reservationId}", {
    onSuccess: () => {
      // Invalider pour assurer la cohérence après mise à jour de présence
      queryClient.invalidateQueries({ queryKey: ["get", "/planning/week"] });
    },
    onError: (err) => {
      console.error("Erreur lors de la mise à jour de l'attendance:", err);
      toast.error("La mise à jour de la présence a échoué", {
        description: "Connexion ou serveur indisponible. Réessayez.",
      });
    },
  });

  const addChildMutation = api.useMutation("post", "/attendance/add-child");
  // NOTE: route recently added; codegen peut ne pas être à jour tant que openapi-react-query n'a pas régénéré les types.
  const deleteReservationMutation = api.useMutation("delete", "/attendance/reservation/{reservationId}", {
    onError: () => {
      toast.error("Suppression impossible", { description: "Veuillez réessayer." });
    },
  });

  // Groupes disponibles pour le filtre: toujours tous les groupes connus
  const groupsForFilter = useMemo(() => {
    if (!ageGroupsData) return [] as { id: string; name: string }[];
    return ageGroupsData.map((g) => ({ id: g.id, name: g.name }));
  }, [ageGroupsData]);

  // Calcul du nombre actuel d'enfants (union dédupliquée serveur + optimistes, sans les ids retirés)
  const effectiveChildrenCount = useMemo(() => {
    const ids = new Set<string>();
    (periodData?.children ?? []).forEach((c) => {
      if (!optimisticRemovedIds.has(c.id)) ids.add(c.id);
    });
    optimisticAdded.forEach((c) => {
      if (!optimisticRemovedIds.has(c.id)) ids.add(c.id);
    });
    return ids.size;
  }, [periodData?.children, optimisticAdded, optimisticRemovedIds]);

  // Pousser la mise à jour vers le parent pour mise à jour en temps réel du calendrier
  const lastPushedCountRef = useRef<number | null>(null);
  useEffect(() => {
    // Ne pas envoyer de mise à jour pendant le chargement initial pour éviter d'afficher 0 temporairement
    if (onChildrenCountChange && open && !isLoading) {
      if (lastPushedCountRef.current !== effectiveChildrenCount) {
        onChildrenCountChange({ periodId, date, count: effectiveChildrenCount });
        lastPushedCountRef.current = effectiveChildrenCount;
      }
    }
  }, [effectiveChildrenCount, onChildrenCountChange, periodId, date, open, isLoading]);

  // Quand on ferme la sheet, envoyer une dernière valeur pour s'assurer de la sync finale
  useEffect(() => {
    // Ne pas envoyer de mise à jour si on est en train de charger
    if (!open && onChildrenCountChange && !isLoading) {
      onChildrenCountChange({ periodId, date, count: effectiveChildrenCount });
      lastPushedCountRef.current = effectiveChildrenCount;
    }
  }, [open, onChildrenCountChange, periodId, date, effectiveChildrenCount, isLoading]);

  // IMPORTANT: Réinitialiser tout l'état optimiste quand on change de période ou de date
  useEffect(() => {
    // Nettoyage des états qui ne doivent pas fuiter d'une période à l'autre
    setTimeout(() => {
      setOptimisticAttendance({});
      setOptimisticAdded([]);
      setRecentlyAddedIds(new Set());
      setOptimisticRemovedIds(new Set());
      setAdHocOrder({});
      adHocOrderCounter.current = 0;
      setStableChildrenOrder([]);
      setScrollTargetChildId(undefined);
      childRefs.current = {};
      lastPushedCountRef.current = null;
    }, 0);
  }, [periodId, date]);

  // Sauvegarder l'ordre initial des enfants à l'ouverture de la sheet
  useEffect(() => {
    if (open && periodData?.children && stableChildrenOrder.length === 0) {
      // Trier initialement par présence : present → absent → pending
      const sorted = [...periodData.children].sort((a, b) => {
        const order = { present: 0, absent: 1, pending: 2 };
        return order[a.attendance] - order[b.attendance];
      });

      setTimeout(() => {
        setStableChildrenOrder(sorted.map((child) => child.id));
        // Initialiser un ordre pour les adHoc existants (si présents) afin de préserver leur stabilité
        const initial: Record<string, number> = {};
        sorted.forEach((c) => {
          // adHoc peut ne pas être présent selon génération actuelle; cast léger
          const maybeAdHoc = (c as Partial<ChildWithAttendance>).adHoc;
          if (maybeAdHoc) {
            adHocOrderCounter.current += 1;
            initial[c.id] = adHocOrderCounter.current;
          }
        });
        if (Object.keys(initial).length) setAdHocOrder(initial);
      }, 0);
    }
  }, [open, periodData?.children, stableChildrenOrder.length]);

  // Réinitialiser l'ordre stable quand la sheet se ferme
  useEffect(() => {
    if (!open) {
      setTimeout(() => setStableChildrenOrder([]), 0);
    }
  }, [open]); // Enfants filtrés par groupe sélectionné avec ordre stable
  const filteredChildren = useMemo(() => {
    const serverChildren = periodData?.children ?? [];
    // Fusion enfants serveur + ajouts optimistes (éviter doublons via id)
    const mergedMap = new Map<string, ChildWithAttendance>();
    type MaybeAdHoc = Omit<ChildWithAttendance, "adHoc"> & { adHoc?: boolean };
    (serverChildren as MaybeAdHoc[]).forEach((c) => mergedMap.set(c.id, { ...(c as ChildWithAttendance), adHoc: c.adHoc ?? false }));
    optimisticAdded.forEach((c) => {
      if (!mergedMap.has(c.id)) mergedMap.set(c.id, c);
    });
    let children = Array.from(mergedMap.values()).filter((c) => !optimisticRemovedIds.has(c.id));

    if (selectedGroup !== "all") {
      children = children.filter((c) => (selectedGroup === "no-group" ? !c.groupId : c.groupId === selectedGroup));
    }

    // Recherche très permissive: prénom, nom, et âge (ex: "zoé mar 6", "6 ans")
    if (searchQuery.trim()) {
      const normalize = (s: string) =>
        s
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "") // enlever les accents
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, " ")
          .replace(/\s+/g, " ")
          .trim();

      const qTokens = normalize(searchQuery).split(" ").filter(Boolean);

      children = children.filter((c) => {
        const fullName = `${c.firstName || ""} ${c.lastName || ""}`.trim();
        const ageFromField = typeof c.age === "number" && isFinite(c.age) && c.age > 0 ? c.age : undefined;
        const computedAge = !ageFromField && c.birthDate ? differenceInYears(new Date(), new Date(c.birthDate)) : undefined;
        const age = (ageFromField ?? computedAge) as number | undefined;

        const haystackParts: string[] = [fullName];
        if (typeof age === "number" && isFinite(age)) {
          haystackParts.push(String(age));
          haystackParts.push(`${age} ans`);
        }

        const haystack = normalize(haystackParts.join(" "));
        return qTokens.every((t) => haystack.includes(t));
      });
    }

    if (stableChildrenOrder.length > 0) {
      children = children.slice().sort((a, b) => {
        const ai = stableChildrenOrder.indexOf(a.id);
        const bi = stableChildrenOrder.indexOf(b.id);
        // Si les deux sont inconnus (ajouts récents simultanés), garder l'ordre actuel
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1; // élément inconnu va à la fin
        if (bi === -1) return -1;
        return ai - bi;
      });
    }
    return children;
  }, [periodData?.children, selectedGroup, stableChildrenOrder, optimisticAdded, optimisticRemovedIds, searchQuery]);

  // Grouper les enfants par groupe d'âge en préservant l'ordre stable
  const childrenByGroup = useMemo(() => {
    const groups: { [key: string]: ChildWithAttendance[] } = {};
    // Initialiser tous les groupes connus (même vides)
    (ageGroupsData ?? []).forEach((g) => {
      groups[g.id] = [];
    });

    const optimisticIds = new Set(optimisticAdded.map((c) => c.id));
    filteredChildren.forEach((child) => {
      if (!child.groupId || !groups[child.groupId]) return; // ignorer les enfants sans groupe
      const list = groups[child.groupId];
      // Inject attendance optimiste si présent
      const effectiveAttendance = optimisticAttendance[child.id] ?? child.attendance;
      list.push({ ...child, attendance: effectiveAttendance });
    });
    // Réorganiser: enfants adHoc (optimistes ou existants) toujours en bas, dans l'ordre d'insertion
    Object.keys(groups).forEach((gid) => {
      const list = groups[gid];
      const adHocList: ChildWithAttendance[] = [];
      const regularList: ChildWithAttendance[] = [];
      list.forEach((c) => (optimisticIds.has(c.id) || c.adHoc ? adHocList.push(c) : regularList.push(c)));
      adHocList.sort((a, b) => (adHocOrder[a.id] ?? 0) - (adHocOrder[b.id] ?? 0));
      groups[gid] = [...regularList, ...adHocList];
    });
    return groups;
  }, [filteredChildren, optimisticAttendance, optimisticAdded, adHocOrder, ageGroupsData]);

  const handleAttendanceChange = useCallback(
    (childId: string, status: AttendanceStatus) => {
      // Chercher d'abord côté serveur puis dans les ajouts optimistes
      const serverChild = periodData?.children.find((c) => c.id === childId);
      const optimisticChild = optimisticAdded.find((c) => c.id === childId);
      const child = serverChild || optimisticChild;
      if (!child) return;

      // Mise à jour immédiate (optimistic UI)
      setOptimisticAttendance((prev) => ({ ...prev, [childId]: status }));

      const isPlaceholder = child.reservationId.startsWith("optimistic-");
      if (isPlaceholder) {
        // Réservation pas encore confirmée, on attendra le succès d'ajout pour pousser au serveur
        return;
      }

      updateAttendanceMutation.mutate(
        {
          params: { path: { reservationId: child.reservationId } },
          body: { attendance: status },
        },
        {
          onError: () => {
            // rollback à l'état serveur ou pending précédent
            setOptimisticAttendance((prev) => ({ ...prev, [childId]: child.attendance }));
          },
        }
      );
    },
    [periodData?.children, optimisticAdded, updateAttendanceMutation]
  );

  // Note: plus de flush par effet; on pousse l'état souhaité dès que l'API renvoie la vraie réservation (voir handleChildAdd)

  const handleChildAdd = useCallback(
    async (childId: string, sourceGroupId?: string, fallbackChild?: ChildBase) => {
      // Récupérer données enfant depuis periodData; sinon utiliser le fallback fourni (ex: enfant nouvellement créé)
      const available = periodData?.availableChildren?.find((c) => c.id === childId);

      // Déterminer les infos de base pour le placeholder optimiste
      const base = available
        ? {
            id: available.id,
            firstName: available.firstName,
            lastName: available.lastName,
            groupId: available.groupId,
            groupName: available.groupName,
            // Pas de birthDate dans AvailableChild: on mettra un placeholder ci-dessous
            birthDate: undefined as string | undefined,
            age: available.age,
            gender: undefined as string | undefined,
            notes: "",
            leaveAlone: false,
            imageRights: false,
          }
        : fallbackChild
          ? {
              id: fallbackChild.id,
              firstName: fallbackChild.firstName,
              lastName: fallbackChild.lastName,
              groupId: fallbackChild.groupId,
              groupName: fallbackChild.groupName || (ageGroupsData?.find((g) => g.id === fallbackChild.groupId)?.name as string | undefined),
              birthDate: fallbackChild.birthDate,
              age: differenceInYears(new Date(), new Date(fallbackChild.birthDate)),
              gender: fallbackChild.gender as string | undefined,
              notes: fallbackChild.notes || "",
              leaveAlone: !!fallbackChild.leaveAlone,
              imageRights: !!fallbackChild.imageRights,
            }
          : undefined;

      if (!base) return; // Rien à faire si on ne trouve rien et pas de fallback

      // Créer placeholder optimiste en réutilisant la mécanique existante
      const optimisticChild: ChildWithAttendance = {
        id: base.id,
        firstName: base.firstName,
        lastName: base.lastName,
        age: typeof base.age === "number" ? base.age : 0,
        birthDate: base.birthDate || new Date().toISOString(),
        notes: base.notes || "",
        leaveAlone: base.leaveAlone ?? false,
        imageRights: base.imageRights ?? false,
        gender: base.gender,
        groupId: base.groupId,
        groupName: base.groupName,
        reservationId: `optimistic-${base.id}`,
        attendance: "pending",
        adHoc: true,
      } as ChildWithAttendance;

      setOptimisticAdded((prev) => [...prev, optimisticChild]);
      // Ajouter dans l'ordre stable pour qu'il reste à la fin de manière persistante
      setStableChildrenOrder((prev) => (prev.includes(optimisticChild.id) ? prev : [...prev, optimisticChild.id]));
      setRecentlyAddedIds((prev) => new Set(prev).add(optimisticChild.id));
      setAdHocOrder((prev) => {
        if (prev[optimisticChild.id] !== undefined) return prev;
        adHocOrderCounter.current += 1;
        return { ...prev, [optimisticChild.id]: adHocOrderCounter.current };
      });
      // Si l'enfant appartient à un autre groupe que celui où le bouton a été cliqué, préparer le scroll
      if (sourceGroupId && base.groupId && base.groupId !== sourceGroupId) {
        setScrollTargetChildId(optimisticChild.id);
      }
      // Retirer automatiquement le marquage visuel après 5s
      setTimeout(() => {
        setRecentlyAddedIds((prev) => {
          if (!prev.has(optimisticChild.id)) return prev;
          const clone = new Set(prev);
          clone.delete(optimisticChild.id);
          return clone;
        });
      }, 5000);

      try {
        const res = await addChildMutation.mutateAsync({
          body: { childId, periodId, date },
        });
        if (res?.child) {
          // Remplacer le placeholder par les données renvoyées par le serveur
          setOptimisticAdded((prev) => prev.map((c) => (c.id === optimisticChild.id ? (res.child as ChildWithAttendance) : c)));
          // Si l'utilisateur avait choisi un statut de présence avant la confirmation, pousser au serveur maintenant
          const desired = optimisticAttendance[optimisticChild.id];
          if (desired && desired !== (res.child as ChildWithAttendance).attendance) {
            updateAttendanceMutation.mutate({
              params: { path: { reservationId: (res.child as ChildWithAttendance).reservationId } },
              body: { attendance: desired },
            });
          }
          // Invalider les queries pour assurer la cohérence du cache
          queryClient.invalidateQueries({ queryKey: ["get", "/attendance/period/{periodId}"] });
          queryClient.invalidateQueries({ queryKey: ["get", "/planning/week"] });
        } else {
          // Par sécurité, re-synchroniser
          queryClient.invalidateQueries({ queryKey: ["get", "/attendance/period/{periodId}"] });
          queryClient.invalidateQueries({ queryKey: ["get", "/planning/week"] });
        }
      } catch (error) {
        console.error("Erreur lors de l'ajout de l'enfant:", error);
        const { status, message } = getHttpStatusAndMessage(
          error as { status?: number; response?: { status?: number }; data?: { message?: string }; message?: string } | Error | string | null | undefined
        );
        if (status === 409 || message?.toLowerCase?.().includes("already reserved")) {
          toast.error("Déjà réservé", { description: "Cet enfant est déjà réservé pour cette période." });
        } else {
          toast.error("Impossible d'ajouter l'enfant", { description: "Veuillez réessayer." });
        }
        // Rollback: retirer placeholder
        setOptimisticAdded((prev) => prev.filter((c) => c.id !== optimisticChild.id));
        setRecentlyAddedIds((prev) => {
          const clone = new Set(prev);
          clone.delete(optimisticChild.id);
          return clone;
        });
      }
    },
    [addChildMutation, periodId, date, periodData?.availableChildren, ageGroupsData, queryClient, optimisticAttendance, updateAttendanceMutation]
  );

  // Effect pour scroller vers la carte ajoutée une fois présente dans le DOM
  useEffect(() => {
    if (!scrollTargetChildId) return;
    // Attendre le prochain frame pour garantir le layout
    requestAnimationFrame(() => {
      const el = childRefs.current[scrollTargetChildId!];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setScrollTargetChildId(undefined);
      }
    });
  }, [filteredChildren, scrollTargetChildId]);

  const handleRemoveChild = useCallback(
    (child: ChildWithAttendance) => {
      if (!child.adHoc) return;
      const isServerChild = !child.reservationId.startsWith("optimistic-");
      const previousOrder = stableChildrenOrder.slice();

      // Marquer comme retiré instantanément
      setOptimisticRemovedIds((prev) => new Set(prev).add(child.id));
      setOptimisticAdded((prev) => prev.filter((c) => c.id !== child.id));
      setStableChildrenOrder((prev) => prev.filter((id) => id !== child.id));
      setAdHocOrder((prev) => {
        if (!(child.id in prev)) return prev;
        const clone = { ...prev };
        delete clone[child.id];
        return clone;
      });
      toast.success(`${child.firstName} ${child.lastName} retiré de la période`);

      if (isServerChild) {
        deleteReservationMutation.mutate(
          { params: { path: { reservationId: child.reservationId } } },
          {
            onError: () => {
              // rollback complet
              setOptimisticRemovedIds((prev) => {
                const clone = new Set(prev);
                clone.delete(child.id);
                return clone;
              });
              // Réintégrer dans ordre stable si besoin
              setStableChildrenOrder(previousOrder);
              // Invalidates to resync just in case
              queryClient.invalidateQueries({ queryKey: ["get", "/attendance/period/{periodId}"] });
              queryClient.invalidateQueries({ queryKey: ["get", "/planning/week"] });
              toast.error(`Suppression échouée pour ${child.firstName} ${child.lastName}`, { description: "Veuillez réessayer." });
            },
            onSuccess: () => {
              // Assurer cohérence finale (au cas où d'autres données changent)
              queryClient.invalidateQueries({ queryKey: ["get", "/attendance/period/{periodId}"] });
              queryClient.invalidateQueries({ queryKey: ["get", "/planning/week"] });
            },
          }
        );
      }
    },
    [deleteReservationMutation, queryClient, stableChildrenOrder]
  );

  // Build ordered groups for rendering (preserve screenshot ordering)
  const orderedGroupEntries = useMemo(() => {
    const entries = Object.entries(childrenByGroup);
    // Appliquer le filtre sélectionné: soit tous, soit un seul groupe
    const filteredEntries = entries.filter(([gid]) => {
      if (selectedGroup === "all") return true;
      return gid === selectedGroup;
    });

    const order = (ageGroupsData ?? []).map((g) => g.id);
    const idx = (gid: string) => order.indexOf(gid);
    return filteredEntries.sort((a, b) => {
      const ia = idx(a[0]);
      const ib = idx(b[0]);
      const sa = ia === -1 ? Number.MAX_SAFE_INTEGER : ia;
      const sb = ib === -1 ? Number.MAX_SAFE_INTEGER : ib;
      return sa - sb;
    });
  }, [childrenByGroup, selectedGroup, ageGroupsData]);

  if (isLoading) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="h-screen max-w-1/2! overflow-y-auto">
          {/* A11y: Provide a hidden title/description for Radix Dialog requirements */}
          <SheetTitle className="sr-only">Chargement de la fiche d'appel</SheetTitle>
          <SheetDescription className="sr-only">Chargement en cours des données de présence</SheetDescription>
          <SheetHeader className="sticky top-0 z-2 flex flex-row items-center justify-between border-b bg-white p-0 shadow-xs">
            <div className="p-5 text-xl font-bold">Chargement...</div>
            <div className="mr-4">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
              <p className="mt-2 text-sm text-muted-foreground">Chargement de la fiche d'appel...</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (error) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="h-screen max-w-1/2! overflow-y-auto">
          {/* A11y: Provide a hidden title/description for Radix Dialog requirements */}
          <SheetTitle className="sr-only">Erreur fiche d'appel</SheetTitle>
          <SheetDescription className="sr-only">Erreur lors du chargement des données de présence</SheetDescription>
          <SheetHeader className="sticky top-0 z-2 flex flex-row items-center justify-between border-b bg-white p-0 shadow-xs">
            <div className="p-5 text-xl font-bold">Erreur</div>
            <div className="mr-4">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>
          <div className="p-10 pt-2">
            <div className="text-center text-red-600">Erreur lors du chargement des données : {error?.message || "Erreur inconnue"}</div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="h-screen max-w-1/2! overflow-y-auto">
        {/* A11y: Provide a hidden title/description for Radix Dialog requirements */}
        <SheetTitle className="sr-only">
          {periodDate} · {periodTitle}
        </SheetTitle>
        <SheetDescription className="sr-only">
          Prise de présence pour {periodTitle} — {periodDate}
        </SheetDescription>
        <SheetHeader className="sticky top-0 z-2 flex flex-row items-center justify-between border-b bg-white p-0 shadow-xs">
          <div className="p-5 text-xl font-bold">
            {periodDate} · {periodTitle}
          </div>
          {/* Right cluster: large search + close button tightly grouped */}
          <div className="mr-4 flex items-center gap-2">
            <Input placeholder="Rechercher un enfant" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-[18vw]" />
            <Button aria-label="Fermer" variant="ghost" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>
        <div className="p-10 pt-2">
          <div className="mb-6 flex flex-col gap-6">
            <div className="flex flex-col justify-between">
              <div className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-muted-foreground">Groupes :</span>
                  <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                    <SelectTrigger className="h-9 max-w-[200px] text-sm">
                      <SelectValue placeholder="Tous" />
                    </SelectTrigger>
                    <SelectContent align="end">
                      <SelectItem value="all">Tous</SelectItem>
                      {groupsForFilter.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.name}
                        </SelectItem>
                      ))}
                      {/* 'Sans groupe' retiré: tout enfant doit appartenir à un groupe */}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  size="sm"
                  className="bg-blue-600 font-medium text-white hover:bg-blue-600/90"
                  onClick={async () => {
                    try {
                      const orgName = activeOrg?.name || "Club";
                      // Logo potentiellement défini via onboarding; propriété non typée sur OrganizationBase -> cast léger désambigué
                      const orgLogo = (activeOrg && (activeOrg as { logo?: string }).logo) || undefined;
                      const periodLabel = `${periodDate} · ${periodTitle}`;
                      // Extraction heure début/fin à partir de periodTime si format "HH:MM - HH:MM"
                      let start: string | undefined;
                      let end: string | undefined;
                      if (periodTime && /\d{2}:\d{2}\s?-\s?\d{2}:\d{2}/.test(periodTime)) {
                        const parts = periodTime.split(/-|–/).map((p: string) => p.trim());
                        start = parts[0];
                        end = parts[1];
                      }
                      // Préparation des enfants avec les téléphones des parents
                      const pdfChildren = filteredChildren;
                      const { generateAttendancePdf } = await import("./pdf/generate-attendance-pdf");
                      await generateAttendancePdf({
                        clubName: orgName,
                        clubLogo: orgLogo,
                        periodLabel,
                        date,
                        startTime: start,
                        endTime: end,
                        periodType: periodTitle,
                        children: pdfChildren,
                      });
                      toast.success("PDF généré", { description: "Téléchargement de la fiche d'appel" });
                    } catch (e) {
                      console.error(e);
                      toast.error("Generation PDF échouée", { description: "Réessayez plus tard." });
                    }
                  }}
                >
                  <Printer className="mr-2 h-4 w-4" />
                  Imprimer la fiche d'appel
                </Button>
              </div>
            </div>
          </div>
          <div className="space-y-10">
            {orderedGroupEntries.map(([groupId, groupChildren]) => {
              const group = ageGroupsData?.find((g) => g.id === groupId);
              const groupName = group?.name;
              return (
                <div key={groupId}>
                  <div className="mb-4 flex items-baseline gap-3">
                    <h3 className="text-lg font-semibold">{groupName}</h3>
                    <span className="text-sm text-muted-foreground">
                      {groupChildren.length} {groupChildren.length === 1 ? "enfant inscrit" : "enfants inscrits"}
                    </span>
                  </div>
                  <div className="grid auto-rows-fr gap-x-4 gap-y-5 sm:grid-cols-2">
                    {groupChildren.map((child) => {
                      // Détecter si cet enfant spécifique est en cours de mise à jour
                      const isChildUpdating =
                        updateAttendanceMutation.isPending && updateAttendanceMutation.variables?.params?.path?.reservationId === child.reservationId;
                      const isNew = recentlyAddedIds.has(child.id);
                      return (
                        <div
                          key={child.id}
                          ref={(el) => {
                            if (el) childRefs.current[child.id] = el;
                          }}
                          data-child-id={child.id}
                        >
                          <ChildAttendanceCard
                            child={child}
                            onAttendanceChange={handleAttendanceChange}
                            isUpdating={isChildUpdating}
                            isNew={isNew}
                            canRemove={child.adHoc === true}
                            onRemove={() => handleRemoveChild(child)}
                            onCardClick={handleViewChild}
                            readOnly={isFutureDay}
                          />
                        </div>
                      );
                    })}
                    <AddChildCombobox
                      targetGroupId={groupId}
                      availableChildren={periodData?.availableChildren || []}
                      onChildAdd={(childId) => handleChildAdd(childId, groupId)}
                      // Exclure tous les enfants déjà réservés dans la période (quel que soit le groupe)
                      // ainsi que les ajouts optimistes non retirés. Cela empêche la recherche d'un enfant déjà inscrit via un autre groupe.
                      excludeIds={[
                        ...(periodData?.children ?? []).filter((c) => !optimisticRemovedIds.has(c.id)).map((c) => c.id),
                        ...optimisticAdded.filter((c) => !optimisticRemovedIds.has(c.id)).map((c) => c.id),
                      ]}
                      onCreateChild={(g) => handleOpenCreateChild(g || groupId)}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="col-span-full mt-1 hidden h-8 justify-start border-dashed text-xs"
                      onClick={() => handleOpenCreateChild(groupId)}
                    >
                      + Créer un nouvel enfant
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SheetContent>
      {/* Sheet enfant rapide */}
      <ChildForm
        open={childFormOpen}
        onOpenChange={setChildFormOpen}
        mode={childFormMode}
        defaultGroupId={childDefaultGroup}
        showTrustedAdults={true}
        editingChild={
          viewingChild
            ? {
                id: viewingChild.id,
                organizationId: "",
                firstName: viewingChild.firstName,
                lastName: viewingChild.lastName,
                birthDate: viewingChild.birthDate,
                notes: viewingChild.notes,
                leaveAlone: viewingChild.leaveAlone,
                imageRights: viewingChild.imageRights,
                gender: viewingChild.gender,
                groupId: viewingChild.groupId,
                groupName: viewingChild.groupName,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              }
            : undefined
        }
        onChildCreated={async (child) => {
          // Ajouter immédiatement à la période via le même mécanisme (UI optimiste conservée)
          handleChildAdd(child.id, undefined, child);
          // Rafraîchir en arrière-plan les données de période pour garantir la cohérence
          queryClient.invalidateQueries({ queryKey: ["get", "/attendance/period/{periodId}"] });
          queryClient.invalidateQueries({ queryKey: ["get", "/planning/week"] });
          setChildFormOpen(false);
        }}
      />
    </Sheet>
  );
}
